import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import Marquee from "react-fast-marquee";
import { ArrowRight, ShieldCheck, UsersThree, LockKey, HandHeart } from "@phosphor-icons/react";
import { MaskedLines, Reveal } from "@/lib/motion";
import { CONTACT } from "@/lib/site";

const IMG = {
  hero: "https://images.unsplash.com/photo-1494303047151-869adc8cceb4?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwyfHxzaWxob3VldHRlJTIwY291cGxlJTIwc2hhZG93fGVufDB8fHx8MTc4NTk0NTgwMXww&ixlib=rb-4.1.0&q=85",
  community: "https://images.unsplash.com/photo-1542460533-50ac46fb13d7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHw0fHxzaWxob3VldHRlJTIwY291cGxlJTIwc2hhZG93fGVufDB8fHx8MTc4NTk0NTgwMXww&ixlib=rb-4.1.0&q=85",
  safety: "https://images.unsplash.com/photo-1483366774565-c783b9f70e2c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2NDJ8MHwxfHNlYXJjaHwxfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwbGlnaHR8ZW58MHx8fHwxNzg1OTQ1ODAxfDA&ixlib=rb-4.1.0&q=85",
};

const CHAPTERS = [
  { n: "01", title: "Discretion by design", body: "Your privacy is the product. We minimise what we collect, never sell your data, and give you full control over your presence." },
  { n: "02", title: "Consent is everything", body: "Every connection begins with mutual, enthusiastic agreement. Coercion, deception, and pressure have no place here." },
  { n: "03", title: "Adults, verified intent", body: "Strictly 18+. We work to keep minors out and to keep the community honest about who they are and what they want." },
  { n: "04", title: "A cleaner classifieds", body: "No illegal content. No paid sexual services. No harassment. Just genuine adults connecting on their own terms." },
];

const PillButton = ({ to, children, dark, testId }) => (
  <Link
    to={to}
    data-testid={testId}
    className={`group relative inline-flex items-center gap-3 overflow-hidden px-8 py-4 text-sm font-semibold uppercase tracking-wider border ${
      dark ? "border-ink text-paper bg-ink" : "border-ink text-ink"
    }`}
  >
    <span className={`relative z-10 transition-colors duration-500 ${dark ? "" : "group-hover:text-paper"}`}>
      {children}
    </span>
    <ArrowRight size={16} className="relative z-10 transition-transform duration-500 group-hover:translate-x-1" />
    {!dark && (
      <span className="absolute inset-0 bg-ink translate-y-full group-hover:translate-y-0 transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)]" />
    )}
  </Link>
);

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex flex-col justify-end pt-28 pb-16 overflow-hidden">
        <div className="mx-auto max-w-[1400px] w-full px-6 md:px-12 grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-oxblood mb-8">
                Interlaced Social LLC — Adults 18+
              </p>
            </Reveal>
            <h1 className="font-serif font-medium tracking-tighter leading-[0.86] text-[15vw] sm:text-[12vw] lg:text-[9.5rem]">
              <MaskedLines
                lines={["Connect", "on your", <>own <span className="italic text-oxblood">terms.</span></>]}
                delay={0.2}
              />
            </h1>
          </div>
          <div className="lg:col-span-4 lg:pb-4">
            <Reveal delay={0.7}>
              <p className="text-lg text-ink-soft leading-relaxed max-w-sm">
                Doublelist is a discreet, adults-only social network and personal
                classifieds platform — built on consent, privacy, and respect.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <PillButton to="/how-it-works" testId="hero-how-it-works">How it works</PillButton>
                <PillButton to="/safety" dark testId="hero-safety">Safety first</PillButton>
              </div>
            </Reveal>
          </div>
        </div>

        {/* clipped parallax image strip */}
        <Reveal delay={0.6} className="mx-auto max-w-[1400px] w-full px-6 md:px-12 mt-14">
          <div className="relative h-[38vh] md:h-[46vh] overflow-hidden border border-line">
            <motion.img
              style={{ y: imgY, scale: imgScale }}
              src={IMG.hero}
              alt="Two people connecting"
              className="clip-frame w-full h-[130%] object-cover object-center -mt-[8%]"
            />
          </div>
        </Reveal>
      </section>

      {/* MARQUEE */}
      <div className="border-y border-line bg-paper py-8">
        <Marquee speed={30} gradient={false} className="overflow-hidden">
          {["Discreet", "Trustworthy", "Authentic", "Secure", "Consensual", "18+ Only"].map((w, i) => (
            <span key={i} className="font-serif text-3xl md:text-5xl tracking-wide uppercase mx-10 flex items-center gap-10">
              {w} <span className="text-oxblood text-2xl">✦</span>
            </span>
          ))}
        </Marquee>
      </div>

      {/* INTRO STATEMENT */}
      <section className="py-24 md:py-36">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <Reveal className="lg:col-span-3">
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-ink-soft">
                (Our promise)
              </p>
            </Reveal>
            <div className="lg:col-span-9">
              <Reveal>
                <p className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight">
                  We built a place for adults to meet without the noise —
                  <span className="text-ink-soft"> free of illegal content, paid sexual services, and harassment.</span> Just
                  real people, clear intentions, and <span className="italic text-oxblood">absolute discretion.</span>
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* MANIFESTO CHAPTERS */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28">
          <Reveal>
            <div className="flex items-end justify-between mb-16">
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-none">
                The <span className="italic text-oxblood">manifesto</span>
              </h2>
              <span className="hidden md:block text-xs uppercase tracking-[0.2em] font-bold text-ink-soft">
                Four principles
              </span>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.08}>
                <div className={`border-t border-ink pt-6 ${i % 2 === 1 ? "md:mt-16" : ""}`}>
                  <span className="font-serif text-6xl md:text-7xl text-oxblood leading-none">{c.n}</span>
                  <h3 className="font-serif text-2xl sm:text-3xl mt-4 mb-3">{c.title}</h3>
                  <p className="text-ink-soft leading-relaxed max-w-md">{c.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* COMMUNITY IMAGE SPLIT */}
      <section className="border-t border-line">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          <Reveal>
            <div className="relative overflow-hidden border border-line aspect-[4/5]">
              <img src={IMG.community} alt="Human connection" className="clip-frame w-full h-full object-cover" />
            </div>
          </Reveal>
          <div>
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-oxblood mb-6">
                A community, not a marketplace
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl tracking-tight leading-[1.02] mb-8">
                Real adults. <br /> Real <span className="italic">intentions.</span>
              </h2>
              <p className="text-ink-soft leading-relaxed text-lg max-w-lg mb-8">
                Doublelist exists for genuine connection between consenting adults.
                We hold the community to clear standards so everyone can explore
                openly, safely, and without judgment.
              </p>
            </Reveal>
            <div className="grid grid-cols-2 gap-8 max-w-md">
              {[
                { icon: UsersThree, label: "Consenting adults only" },
                { icon: LockKey, label: "Privacy-first by default" },
                { icon: HandHeart, label: "Respect & consent" },
                { icon: ShieldCheck, label: "Zero tolerance for abuse" },
              ].map((f, i) => (
                <Reveal key={i} delay={i * 0.06}>
                  <div className="flex flex-col gap-2">
                    <f.icon size={28} weight="light" className="text-oxblood" />
                    <span className="text-sm font-medium">{f.label}</span>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* SAFETY BAND */}
      <section className="bg-ink text-paper">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 py-20 md:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Reveal>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-oxblood mb-6">
                Safety is non-negotiable
              </p>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
                We prohibit what puts people <span className="italic text-oxblood">at risk.</span>
              </h2>
              <p className="text-paper/60 leading-relaxed text-lg mt-8 max-w-xl">
                Illegal content, human trafficking, paid sexual services, minors,
                and harassment are strictly forbidden and actively removed. Report
                anything that crosses the line — we act on it.
              </p>
              <div className="mt-10">
                <Link
                  to="/safety"
                  data-testid="safety-band-cta"
                  className="group inline-flex items-center gap-3 text-sm uppercase tracking-wider font-semibold border-b border-paper/40 pb-1 hover:border-oxblood transition-colors"
                >
                  Read the safety guidelines
                  <ArrowRight size={16} className="transition-transform duration-500 group-hover:translate-x-1" />
                </Link>
              </div>
            </Reveal>
          </div>
          <Reveal delay={0.2} className="lg:col-span-5">
            <div className="relative overflow-hidden border border-paper/20 aspect-[4/3]">
              <img src={IMG.safety} alt="Discretion and boundaries" className="w-full h-full object-cover grayscale" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-36 border-b border-line">
        <div className="mx-auto max-w-[1400px] px-6 md:px-12 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-oxblood mb-8">
              Questions? We're here.
            </p>
            <h2 className="font-serif text-5xl sm:text-6xl lg:text-8xl tracking-tighter leading-[0.9] mb-12">
              Say <span className="italic text-oxblood">hello.</span>
            </h2>
            <div className="flex flex-wrap justify-center gap-4">
              <PillButton to="/contact" dark testId="cta-contact">Contact us</PillButton>
              <PillButton to="/faq" testId="cta-faq">Read the FAQ</PillButton>
            </div>
            <p className="mt-10 text-ink-soft text-sm">{CONTACT.email} · {CONTACT.phone}</p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
